// src/pages/Dashboard.js

import React, { useState, useEffect } from 'react';

import Overview from './components/Overview';
import SalesTab from './components/SalesTab';
import ShopHoursTab from './components/ShopHoursTab';
import ItemsOfferedTab from './components/ItemsOfferedTab';
import ShopInfoTab from './components/ShopInfoTab';
import ShopImageTab from './components/ShopImageTab';
import ReviewsTab from './components/ReviewsTab';
import MessageUsTab from './components/MessageUsTab';
import CommunityChatTab from './components/CommunityChatTab';
import NotificationsTab from './components/NotificationsTab';
import AnalyticsTab from './components/AnalyticsTab';


const GET_MANAGED_SHOPS_API =
    'https://oppiesfunc.azurewebsites.net/api/getManagedShops?code=huECMxImipiK72eFqefelG3Vcf77HzRyN_x22uPFI5UHAzFuRPLsYw==';


function Dashboard() {

    /*
     * =========================================================
     * LOGGED-IN ACCOUNT
     * =========================================================
     *
     * These values identify the actual Firebase / Portal
     * account that logged in.
     *
     * IMPORTANT:
     * accountEmail must NOT change when a manager chooses
     * another shop from the dropdown.
     */

    const accountShopId =
        localStorage.getItem('shopId');

    const accountEmail =
        localStorage.getItem('shopEmail');

    const accountShopName =
        localStorage.getItem('shopName');


    /*
     * =========================================================
     * ACTIVE SHOP
     * =========================================================
     *
     * Normal Op Shop:
     *     active shop = logged-in shop
     *
     * Manager:
     *     active shop = whichever authorised branch is selected.
     */

    const [shopId, setShopId] =
        useState(accountShopId);

    const [shopEmail, setShopEmail] =
        useState(accountEmail);

    const [shopName, setShopName] =
        useState(accountShopName);


    /*
     * =========================================================
     * MANAGER STATE
     * =========================================================
     */

    const [isManager, setIsManager] =
        useState(false);

    const [managerChecked, setManagerChecked] =
        useState(false);

    const [managedShops, setManagedShops] =
        useState([]);

    const [organisation, setOrganisation] =
        useState(null);

    const [managerLoading, setManagerLoading] =
        useState(false);

    const [managerError, setManagerError] =
        useState('');


    /*
     * =========================================================
     * ACTIVE TAB
     * =========================================================
     */

    const [activeTab, setActiveTab] =
        useState('Overview');


    /*
     * =========================================================
     * CHECK MANAGER ACCESS
     * =========================================================
     *
     * We deliberately do NOT rely on a browser value such as:
     *
     * localStorage.manager = true
     *
     * Instead the Dashboard asks getManagedShops.
     *
     * If the account is not a manager the Function returns 403
     * and the Portal behaves exactly like the existing Portal.
     */

    useEffect(() => {

        let cancelled = false;

        const loadManagedShops = async () => {

            if (!accountEmail) {
                setManagerChecked(true);
                return;
            }

            setManagerLoading(true);
            setManagerError('');

            try {

                const separator =
                    GET_MANAGED_SHOPS_API.includes('?')
                        ? '&'
                        : '?';

                const url =
                    `${GET_MANAGED_SHOPS_API}` +
                    `${separator}email=${encodeURIComponent(
                        accountEmail
                    )}`;

                const res = await fetch(url, {
                    method: 'GET',
                    headers: {
                        Accept: 'application/json',
                    },
                    cache: 'no-store',
                });


                /*
                 * -------------------------------------------------
                 * NORMAL OP SHOP
                 * -------------------------------------------------
                 *
                 * A 403 simply means this account does not have
                 * manager access.
                 *
                 * That is NOT treated as a Portal error.
                 */

                if (res.status === 403) {

                    if (!cancelled) {
                        setIsManager(false);
                        setManagedShops([]);
                        setOrganisation(null);
                    }

                    return;
                }


                if (!res.ok) {

                    let errorMessage =
                        `Unable to check manager access (${res.status})`;

                    try {

                        const errorData =
                            await res.json();

                        errorMessage =
                            errorData?.message ||
                            errorData?.error ||
                            errorMessage;

                    } catch {
                        // Keep fallback error message.
                    }

                    throw new Error(errorMessage);
                }


                const data =
                    await res.json();


                /*
                 * Only show manager mode when the backend
                 * explicitly confirms:
                 *
                 * manager === true
                 */

                if (data?.manager !== true) {

                    if (!cancelled) {
                        setIsManager(false);
                        setManagedShops([]);
                        setOrganisation(null);
                    }

                    return;
                }


                const shops =
                    Array.isArray(data.shops)
                        ? data.shops
                        : [];


                if (cancelled) {
                    return;
                }


                setIsManager(true);

                setManagedShops(shops);

                setOrganisation(
                    data.organisation || null
                );


                /*
                 * -------------------------------------------------
                 * CHOOSE INITIAL ACTIVE MANAGED SHOP
                 * -------------------------------------------------
                 *
                 * Preference:
                 *
                 * 1. Previously selected managed shop
                 * 2. Logged-in account's own shop if authorised
                 * 3. First authorised shop
                 */

                const rememberedShopId =
                    localStorage.getItem(
                        'managerSelectedShopId'
                    );


                let selectedShop = null;


                if (rememberedShopId) {

                    selectedShop =
                        shops.find(
                            (shop) =>
                                shop.shopId ===
                                rememberedShopId
                        ) || null;
                }


                if (
                    !selectedShop &&
                    accountShopId
                ) {

                    selectedShop =
                        shops.find(
                            (shop) =>
                                shop.shopId ===
                                accountShopId
                        ) || null;
                }


                if (
                    !selectedShop &&
                    shops.length > 0
                ) {

                    selectedShop =
                        shops[0];
                }


                if (selectedShop) {

                    setShopId(
                        selectedShop.shopId
                    );

                    setShopName(
                        selectedShop.name ||
                        selectedShop.shopId ||
                        accountShopName
                    );

                    setShopEmail(
                        selectedShop.email ||
                        accountEmail
                    );

                    localStorage.setItem(
                        'managerSelectedShopId',
                        selectedShop.shopId
                    );
                }

            } catch (err) {

                console.error(
                    '❌ Failed to check manager access:',
                    err
                );

                if (!cancelled) {

                    /*
                     * Do not break the normal Portal if the
                     * manager lookup itself fails.
                     */

                    setIsManager(false);

                    setManagedShops([]);

                    setOrganisation(null);

                    setManagerError(
                        err.message ||
                        'Unable to load managed shops.'
                    );
                }

            } finally {

                if (!cancelled) {
                    setManagerLoading(false);
                    setManagerChecked(true);
                }
            }
        };


        loadManagedShops();


        return () => {
            cancelled = true;
        };

    }, [
        accountEmail,
        accountShopId,
        accountShopName,
    ]);


    /*
     * =========================================================
     * MANAGER SHOP CHANGE
     * =========================================================
     */

    const handleManagedShopChange = (event) => {

        const selectedShopId =
            event.target.value;


        const selectedShop =
            managedShops.find(
                (shop) =>
                    shop.shopId ===
                    selectedShopId
            );


        /*
         * The dropdown itself only contains authorised shops,
         * but still refuse to change state if no matching shop
         * exists in the authorised response.
         */

        if (!selectedShop) {
            return;
        }


        setShopId(
            selectedShop.shopId
        );

        setShopName(
            selectedShop.name ||
            selectedShop.shopId ||
            'OPPIES Portal'
        );

        setShopEmail(
            selectedShop.email ||
            accountEmail
        );


        /*
         * Remember the manager's branch selection without
         * overwriting the original login identity.
         */

        localStorage.setItem(
            'managerSelectedShopId',
            selectedShop.shopId
        );


        /*
         * shopInfo belongs to the previously active branch.
         *
         * Remove it so the effect below retrieves fresh location
         * information for the newly selected shop.
         */

        localStorage.removeItem(
            'shopInfo'
        );

        console.log(
            '🏪 Manager switched active shop:',
            {
                shopId:
                    selectedShop.shopId,

                name:
                    selectedShop.name,

                email:
                    selectedShop.email,
            }
        );
    };


    /*
     * =========================================================
     * NOTIFICATION DEEP LINK
     * =========================================================
     *
     * Existing behaviour retained.
     *
     * Allows components such as Notifications to navigate the
     * Portal to another tab by dispatching:
     *
     * window.dispatchEvent(
     *     new CustomEvent('oppies:navigate', {
     *         detail: { tab: 'Community Chat' }
     *     })
     * );
     */

    useEffect(() => {

        const handler = (event) => {

            if (event?.detail?.tab) {

                setActiveTab(
                    event.detail.tab
                );
            }
        };


        window.addEventListener(
            'oppies:navigate',
            handler
        );


        return () => {

            window.removeEventListener(
                'oppies:navigate',
                handler
            );
        };

    }, []);


    /*
     * =========================================================
     * SHOP INFO CACHE
     * =========================================================
     *
     * Existing functionality retained.
     *
     * Manager enhancement:
     *
     * shopInfo is now checked against the active shopId.
     *
     * If the manager changes branch, the new branch information
     * is fetched automatically.
     */

    useEffect(() => {

        let cancelled = false;


        const fetchAndStoreShopInfo =
            async () => {

                if (!shopId) {
                    return;
                }


                /*
                 * Check whether the existing cached shopInfo
                 * already belongs to the active shop.
                 */

                try {

                    const cachedRaw =
                        localStorage.getItem(
                            'shopInfo'
                        );


                    if (cachedRaw) {

                        const cached =
                            JSON.parse(
                                cachedRaw
                            );


                        if (
                            cached?.shopId ===
                            shopId
                        ) {

                            return;
                        }
                    }

                } catch {

                    /*
                     * Invalid cached data should simply be replaced.
                     */

                    localStorage.removeItem(
                        'shopInfo'
                    );
                }


                try {

                    const res =
                        await fetch(
                            `https://oppiesfunc.azurewebsites.net/api/getShopById?code=TxEuYhVjo9rIiMlI2EMr8gRFPPRejKrTeKGMbTXwQ--3AzFuHMjRzQ==&shopId=${encodeURIComponent(
                                shopId
                            )}`
                        );


                    if (!res.ok) {

                        throw new Error(
                            `Unable to retrieve shop information (${res.status})`
                        );
                    }


                    const data =
                        await res.json();


                    if (cancelled) {
                        return;
                    }


                    const info = {

                        shopId:
                            data.shopId,

                        name:
                            data.name,

                        lat:
                            data.location?.lat,

                        lon:
                            data.location?.lng,
                    };


                    localStorage.setItem(
                        'shopInfo',
                        JSON.stringify(
                            info
                        )
                    );


                    console.log(
                        '✅ shopInfo set:',
                        info
                    );

                } catch (err) {

                    console.error(
                        '❌ Failed to fetch shop info:',
                        err.message
                    );
                }
            };


        fetchAndStoreShopInfo();


        return () => {
            cancelled = true;
        };

    }, [shopId]);


    /*
     * =========================================================
     * LOGOUT
     * =========================================================
     *
     * Existing behaviour retained for now.
     */

    const handleLogout = () => {

        localStorage.clear();

        window.location.href = '/';
    };


    /*
     * =========================================================
     * PORTAL NAVIGATION
     * =========================================================
     *
     * Existing two-row layout retained.
     */

    const row1 = [
        'Overview',
        'Sales',
        'Shop Hours',
        'Shop Info',
        'Items Offered',
        'Shop Image',
    ];


    const row2 = [
        'Notifications',
        'Community Chat',
        'Analytics',
        'Reviews',
        'Message Us',
        'Logout',
    ];


    /*
     * =========================================================
     * TAB BUTTON
     * =========================================================
     */

    const renderTabButton = (tab) => {

        const isActive =
            activeTab === tab;

        const isLogout =
            tab === 'Logout';


        if (isLogout) {

            return (
                <button
                    key={tab}
                    type="button"
                    className="oppies-dashboard-tab oppies-dashboard-tab-logout"
                    onClick={handleLogout}
                >
                    {tab}
                </button>
            );
        }


        return (
            <button
                key={tab}
                type="button"
                className={
                    isActive
                        ? 'oppies-dashboard-tab oppies-dashboard-tab-active'
                        : 'oppies-dashboard-tab'
                }
                onClick={() =>
                    setActiveTab(tab)
                }
            >
                {tab}
            </button>
        );
    };


    return (
        <>

            <style>{`

                /*
                 * =====================================================
                 * GLOBAL
                 * =====================================================
                 */

                * {
                    box-sizing: border-box;
                }

                html,
                body,
                #root {
                    margin: 0;
                    min-height: 100%;
                }

                body {
                    margin: 0;
                }

                button,
                input,
                textarea,
                select {
                    font: inherit;
                }

                .oppies-dashboard-page {
                    min-height: 100vh;

                    background:
                        radial-gradient(
                            circle at 4% 5%,
                            rgba(4, 120, 87, 0.045),
                            transparent 27%
                        ),
                        radial-gradient(
                            circle at 96% 92%,
                            rgba(253, 246, 227, 0.8),
                            transparent 28%
                        ),
                        #f8faf9;

                    color: #171717;

                    font-family:
                        Inter,
                        -apple-system,
                        BlinkMacSystemFont,
                        "Segoe UI",
                        Roboto,
                        Helvetica,
                        Arial,
                        sans-serif;

                    padding-bottom: 60px;
                }


                /*
                 * =====================================================
                 * TOP HEADER
                 * =====================================================
                 */

                .oppies-dashboard-topbar {
                    width: 100%;

                    background: rgba(255, 255, 255, 0.95);

                    border-bottom: 1px solid #eeeeee;

                    backdrop-filter: blur(18px);
                    -webkit-backdrop-filter: blur(18px);
                }

                .oppies-dashboard-topbar-inner {
                    width: min(1240px, calc(100% - 48px));

                    min-height: 82px;

                    margin: 0 auto;

                    display: flex;
                    align-items: center;
                    justify-content: space-between;

                    gap: 30px;
                }

                .oppies-dashboard-logo-link {
                    display: flex;
                    align-items: center;

                    text-decoration: none;
                }

                .oppies-dashboard-logo {
                    display: block;

                    width: 175px;
                    max-width: 44vw;

                    height: auto;
                }

                .oppies-dashboard-portal-label {
                    display: flex;
                    flex-direction: column;
                    align-items: flex-end;

                    gap: 3px;
                }

                .oppies-dashboard-portal-label-main {
                    color: #171717;

                    font-size: 13px;
                    font-weight: 650;
                }

                .oppies-dashboard-portal-label-sub {
                    color: #8a8f98;

                    font-size: 11px;
                    font-weight: 500;

                    letter-spacing: 0.01em;
                }


                /*
                 * =====================================================
                 * MAIN WRAPPER
                 * =====================================================
                 */

                .oppies-dashboard-main {
                    width: min(1240px, calc(100% - 48px));

                    margin: 0 auto;

                    padding-top: 42px;
                }


                /*
                 * =====================================================
                 * SHOP HEADER
                 * =====================================================
                 */

                .oppies-dashboard-shop-header {
                    margin-bottom: 28px;

                    display: flex;
                    align-items: flex-end;
                    justify-content: space-between;

                    gap: 28px;
                }

                .oppies-dashboard-eyebrow {
                    margin-bottom: 10px;

                    color: #047857;

                    font-size: 11px;
                    font-weight: 750;

                    letter-spacing: 2.6px;
                    text-transform: uppercase;
                }

                .oppies-dashboard-shop-name {
                    margin: 0;

                    color: #171717;

                    font-size: clamp(30px, 4vw, 44px);
                    line-height: 1.04;

                    font-weight: 650;
                    letter-spacing: -2.2px;
                }

                .oppies-dashboard-description {
                    margin: 11px 0 0;

                    max-width: 680px;

                    color: #6b7280;

                    font-size: 15px;
                    line-height: 1.55;
                }

                .oppies-dashboard-status {
                    display: inline-flex;
                    align-items: center;

                    gap: 8px;

                    flex-shrink: 0;

                    padding: 9px 13px;

                    border: 1px solid #dcebe5;
                    border-radius: 999px;

                    background: #f3faf7;

                    color: #047857;

                    font-size: 11px;
                    font-weight: 700;

                    white-space: nowrap;
                }

                .oppies-dashboard-status-dot {
                    width: 7px;
                    height: 7px;

                    border-radius: 50%;

                    background: #059669;
                }


                /*
                 * =====================================================
                 * MANAGER SHOP SELECTOR
                 * =====================================================
                 */

                .oppies-manager-panel {
                    margin-bottom: 24px;

                    padding: 18px 20px;

                    border: 1px solid #dcebe5;
                    border-radius: 22px;

                    background:
                        linear-gradient(
                            135deg,
                            rgba(243, 250, 247, 0.98),
                            rgba(253, 246, 227, 0.52)
                        );

                    box-shadow:
                        0 8px 28px rgba(4, 120, 87, 0.045);
                }

                .oppies-manager-panel-inner {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;

                    gap: 28px;
                }

                .oppies-manager-copy {
                    min-width: 0;
                }

                .oppies-manager-eyebrow {
                    margin-bottom: 5px;

                    color: #047857;

                    font-size: 10px;
                    font-weight: 800;

                    letter-spacing: 2px;
                    text-transform: uppercase;
                }

                .oppies-manager-title {
                    margin: 0;

                    color: #202421;

                    font-size: 18px;
                    line-height: 1.25;

                    font-weight: 700;
                    letter-spacing: -0.45px;
                }

                .oppies-manager-subtitle {
                    margin: 5px 0 0;

                    color: #778079;

                    font-size: 12px;
                    line-height: 1.45;
                }

                .oppies-manager-selector-wrap {
                    width: min(420px, 48%);

                    flex-shrink: 0;
                }

                .oppies-manager-selector-label {
                    display: block;

                    margin-bottom: 6px;

                    color: #56605a;

                    font-size: 10px;
                    font-weight: 750;

                    letter-spacing: 1.35px;
                    text-transform: uppercase;
                }

                .oppies-manager-select {
                    display: block;

                    width: 100%;
                    min-height: 46px;

                    padding:
                        10px
                        42px
                        10px
                        14px;

                    border: 1px solid #cbded6;
                    border-radius: 13px;

                    outline: none;

                    background:
                        #ffffff;

                    color: #222724;

                    font-size: 13px;
                    font-weight: 650;

                    cursor: pointer;

                    transition:
                        border-color 150ms ease,
                        box-shadow 150ms ease,
                        background 150ms ease;
                }

                .oppies-manager-select:hover {
                    border-color: #9fc7b6;
                }

                .oppies-manager-select:focus {
                    border-color: #047857;

                    box-shadow:
                        0 0 0 3px rgba(4, 120, 87, 0.09);
                }

                .oppies-manager-select:disabled {
                    cursor: not-allowed;

                    color: #8c938f;

                    background: #f6f7f6;
                }

                .oppies-manager-warning {
                    margin-top: 10px;

                    color: #996128;

                    font-size: 11px;
                    line-height: 1.45;
                }


                /*
                 * =====================================================
                 * NAVIGATION SHELL
                 * =====================================================
                 */

                .oppies-dashboard-navigation {
                    margin-bottom: 24px;

                    padding: 14px;

                    border: 1px solid #ebebe8;
                    border-radius: 24px;

                    background: rgba(255, 255, 255, 0.93);

                    box-shadow:
                        0 8px 26px rgba(20, 25, 23, 0.035);
                }

                .oppies-dashboard-tab-row {
                    display: grid;

                    grid-template-columns:
                        repeat(6, minmax(0, 1fr));

                    gap: 9px;
                }

                .oppies-dashboard-tab-row
                + .oppies-dashboard-tab-row {
                    margin-top: 9px;
                }

                .oppies-dashboard-tab {
                    min-height: 43px;

                    padding: 8px 10px;

                    border: 1px solid #e8e9e7;
                    border-radius: 13px;

                    background: #f7f8f7;

                    color: #555b61;

                    font-size: 12px;
                    font-weight: 650;

                    cursor: pointer;

                    white-space: nowrap;

                    transition:
                        background 150ms ease,
                        color 150ms ease,
                        border-color 150ms ease,
                        box-shadow 150ms ease,
                        transform 100ms ease;
                }

                .oppies-dashboard-tab:hover {
                    border-color: #cfdcd6;

                    background: #f1f7f4;

                    color: #047857;
                }

                .oppies-dashboard-tab:active {
                    transform: scale(0.985);
                }

                .oppies-dashboard-tab-active {
                    border-color: #047857;

                    background: #047857;

                    color: #ffffff;

                    box-shadow:
                        0 5px 13px rgba(4, 120, 87, 0.18);
                }

                .oppies-dashboard-tab-active:hover {
                    border-color: #065f46;

                    background: #065f46;

                    color: #ffffff;
                }

                .oppies-dashboard-tab-logout {
                    border-color: #ece7e3;

                    background: #f8f6f4;

                    color: #78716c;
                }

                .oppies-dashboard-tab-logout:hover {
                    border-color: #efd2ca;

                    background: #fff5f2;

                    color: #b5472c;
                }


                /*
                 * =====================================================
                 * TAB CONTENT
                 * =====================================================
                 */

                .oppies-dashboard-content-shell {
                    position: relative;

                    width: 100%;

                    min-height: 420px;

                    padding: clamp(22px, 3vw, 34px);

                    border: 1px solid #eeeeeb;
                    border-radius: 26px;

                    background: #ffffff;

                    box-shadow:
                        0 16px 44px rgba(28, 35, 32, 0.045),
                        0 2px 7px rgba(28, 35, 32, 0.025);

                    overflow: hidden;
                }

                .oppies-dashboard-content-shell::before {
                    content: "";

                    position: absolute;

                    top: 0;
                    left: 0;

                    width: 100%;
                    height: 3px;

                    background:
                        linear-gradient(
                            90deg,
                            #047857,
                            #14b87a,
                            rgba(20, 184, 122, 0)
                        );
                }

                .oppies-dashboard-content {
                    position: relative;
                    z-index: 1;
                }


                /*
                 * =====================================================
                 * FOOTER
                 * =====================================================
                 */

                .oppies-dashboard-footer {
                    width: min(1240px, calc(100% - 48px));

                    margin: 36px auto 0;

                    padding-top: 22px;

                    border-top: 1px solid #e9eae8;

                    display: flex;
                    justify-content: space-between;
                    align-items: center;

                    gap: 24px;

                    color: #92979d;

                    font-size: 11px;
                }

                .oppies-dashboard-footer a {
                    color: #047857;

                    font-weight: 650;

                    text-decoration: none;
                }

                .oppies-dashboard-footer a:hover {
                    text-decoration: underline;
                }


                /*
                 * =====================================================
                 * SMALL DESKTOP / TABLET
                 * =====================================================
                 */

                @media (max-width: 1050px) {

                    .oppies-dashboard-tab-row {
                        grid-template-columns:
                            repeat(3, minmax(0, 1fr));
                    }

                    .oppies-dashboard-shop-header {
                        align-items: flex-start;
                    }

                    .oppies-manager-panel-inner {
                        align-items: flex-start;
                    }

                    .oppies-manager-selector-wrap {
                        width: min(390px, 52%);
                    }
                }


                /*
                 * =====================================================
                 * MOBILE
                 * =====================================================
                 */

                @media (max-width: 650px) {

                    .oppies-dashboard-page {
                        padding-bottom: 35px;
                    }

                    .oppies-dashboard-topbar-inner,
                    .oppies-dashboard-main,
                    .oppies-dashboard-footer {
                        width: min(100% - 28px, 1240px);
                    }

                    .oppies-dashboard-topbar-inner {
                        min-height: 70px;
                    }

                    .oppies-dashboard-logo {
                        width: 145px;
                    }

                    .oppies-dashboard-portal-label {
                        display: none;
                    }

                    .oppies-dashboard-main {
                        padding-top: 30px;
                    }

                    .oppies-dashboard-shop-header {
                        margin-bottom: 22px;

                        flex-direction: column;
                        align-items: flex-start;

                        gap: 16px;
                    }

                    .oppies-dashboard-shop-name {
                        font-size: 34px;
                        letter-spacing: -1.8px;
                    }

                    .oppies-dashboard-description {
                        font-size: 14px;
                    }

                    .oppies-manager-panel {
                        margin-bottom: 20px;

                        padding: 16px;

                        border-radius: 18px;
                    }

                    .oppies-manager-panel-inner {
                        flex-direction: column;

                        gap: 16px;
                    }

                    .oppies-manager-selector-wrap {
                        width: 100%;
                    }

                    .oppies-manager-select {
                        min-height: 45px;
                    }

                    .oppies-dashboard-navigation {
                        padding: 10px;

                        border-radius: 19px;
                    }

                    .oppies-dashboard-tab-row {
                        grid-template-columns:
                            repeat(2, minmax(0, 1fr));

                        gap: 8px;
                    }

                    .oppies-dashboard-tab-row
                    + .oppies-dashboard-tab-row {
                        margin-top: 8px;
                    }

                    .oppies-dashboard-tab {
                        min-height: 42px;

                        padding: 8px 6px;

                        font-size: 11.5px;

                        white-space: normal;
                    }

                    .oppies-dashboard-content-shell {
                        min-height: 350px;

                        padding: 18px 15px;

                        border-radius: 20px;
                    }

                    .oppies-dashboard-footer {
                        margin-top: 28px;

                        padding-top: 18px;

                        flex-direction: column;
                        align-items: flex-start;

                        gap: 6px;
                    }
                }


                /*
                 * =====================================================
                 * VERY SMALL DEVICES
                 * =====================================================
                 */

                @media (max-width: 390px) {

                    .oppies-dashboard-tab-row {
                        grid-template-columns: 1fr 1fr;
                    }

                    .oppies-dashboard-shop-name {
                        font-size: 31px;
                    }
                }

            `}</style>


            <div className="oppies-dashboard-page">


                {/* =====================================================
                    TOP HEADER
                   ===================================================== */}

                <header className="oppies-dashboard-topbar">

                    <div className="oppies-dashboard-topbar-inner">

                        <a
                            href="https://www.oppies.nz"
                            className="oppies-dashboard-logo-link"
                            aria-label="Visit OPPIES"
                        >
                            <img
                                src="/app_logo.png"
                                alt="OPPIES"
                                className="oppies-dashboard-logo"
                            />
                        </a>


                        <div className="oppies-dashboard-portal-label">

                            <span className="oppies-dashboard-portal-label-main">
                                Op Shop Online Portal
                            </span>

                        </div>

                    </div>

                </header>


                {/* =====================================================
                    MAIN
                   ===================================================== */}

                <main className="oppies-dashboard-main">


                    {/* -------------------------------------------------
                        SHOP INTRO
                       ------------------------------------------------- */}

                    <section className="oppies-dashboard-shop-header">

                        <div>

                            <div className="oppies-dashboard-eyebrow">

                                {isManager
                                    ? 'Managing Op Shop'
                                    : 'Your Op Shop'
                                }

                            </div>


                            <h1 className="oppies-dashboard-shop-name">
                                {shopName || 'OPPIES Portal'}
                            </h1>


                            <p className="oppies-dashboard-description">

                                {isManager
                                    ? 'Manage the selected Op Shop profile, shop information, sales, community activity and customer engagement.'
                                    : 'Manage your OPPIES profile, shop information, sales, community activity and customer engagement.'
                                }

                            </p>

                        </div>


                        <div className="oppies-dashboard-status">

                            <span className="oppies-dashboard-status-dot" />

                            Portal Access Active

                        </div>

                    </section>


                    {/* =================================================
                        MANAGER SHOP SELECTOR
                       =================================================
                       
                       ONLY rendered when getManagedShops explicitly
                       returns manager: true.
                       ================================================= */}

                    {isManager && (

                        <section
                            className="oppies-manager-panel"
                            aria-label="Organisation shop selector"
                        >

                            <div className="oppies-manager-panel-inner">


                                <div className="oppies-manager-copy">

                                    <div className="oppies-manager-eyebrow">
                                        Organisation Access
                                    </div>


                                    <h2 className="oppies-manager-title">

                                        {organisation?.name ||
                                            'Managed Op Shops'
                                        }

                                    </h2>


                                    <p className="oppies-manager-subtitle">

                                        Choose an authorised shop to manage.
                                        Changes made in the Portal will apply
                                        to the selected shop.

                                    </p>

                                </div>


                                <div className="oppies-manager-selector-wrap">

                                    <label
                                        className="oppies-manager-selector-label"
                                        htmlFor="oppies-managed-shop"
                                    >
                                        Active Op Shop
                                    </label>


                                    <select
                                        id="oppies-managed-shop"
                                        className="oppies-manager-select"
                                        value={shopId || ''}
                                        onChange={handleManagedShopChange}
                                        disabled={
                                            managerLoading ||
                                            managedShops.length === 0
                                        }
                                    >

                                        {managedShops.length === 0 ? (

                                            <option value="">
                                                No authorised shops available
                                            </option>

                                        ) : (

                                            managedShops.map(
                                                (shop) => (

                                                    <option
                                                        key={shop.shopId}
                                                        value={shop.shopId}
                                                    >

                                                        {shop.name ||
                                                            shop.shopId
                                                        }

                                                    </option>
                                                )
                                            )

                                        )}

                                    </select>


                                    {managerError && (

                                        <div className="oppies-manager-warning">
                                            {managerError}
                                        </div>

                                    )}

                                </div>

                            </div>

                        </section>

                    )}


                    {/* -------------------------------------------------
                        NAVIGATION
                       ------------------------------------------------- */}

                    <nav
                        className="oppies-dashboard-navigation"
                        aria-label="Portal navigation"
                    >

                        <div className="oppies-dashboard-tab-row">
                            {row1.map(renderTabButton)}
                        </div>


                        <div className="oppies-dashboard-tab-row">
                            {row2.map(renderTabButton)}
                        </div>

                    </nav>


                    {/* -------------------------------------------------
                        TAB CONTENT
                       ------------------------------------------------- */}

                    <section className="oppies-dashboard-content-shell">

                        <div className="oppies-dashboard-content">


                            {activeTab === 'Overview' && (

                                <Overview
                                    shopEmail={shopEmail}
                                />

                            )}


                            {activeTab === 'Sales' && (

                                <SalesTab
                                    shopId={shopId}
                                    userEmail={accountEmail}
                                />

                            )}


                            {activeTab === 'Shop Hours' && (

                                <ShopHoursTab
                                    shopId={shopId}
                                    userEmail={accountEmail}
                                />

                            )}


                            
                             { activeTab === 'Shop Info' && (

                                    <ShopInfoTab
                                        shopId={shopId}
                                        userEmail={accountEmail}
                                    />

                                )}


                            {activeTab === 'Items Offered' && (

                                <ItemsOfferedTab
                                    shop={{
                                        shopId
                                    }}
                                    userEmail={
                                        accountEmail
                                    }
                                />

                            )}


                            {activeTab === 'Shop Image' && (

                                <ShopImageTab
                                    shopId={shopId}
                                    userEmail={accountEmail}
                                />

                            )}


                            {activeTab === 'Notifications' && (

                                <NotificationsTab />

                            )}


                            {activeTab === 'Community Chat' && (

                                <CommunityChatTab
                                    shopId={shopId}
                                    shopEmail={shopEmail}
                                    shopName={shopName}
                                    userEmail={accountEmail}
                                />

                            )}


                            {activeTab === 'Analytics' && (

                                <AnalyticsTab />

                            )}


                            {activeTab === 'Reviews' && (

                                <ReviewsTab
                                    shopId={shopId}
                                />

                            )}


                            {activeTab === 'Message Us' && (

                                <MessageUsTab
                                    shop={{
                                        shopId,
                                        email: shopEmail,
                                        name: shopName,
                                    }}
                                    userEmail={accountEmail}
                                    isManager={isManager}
                                />

                            )}

                        </div>

                    </section>

                </main>


                {/* =====================================================
                    FOOTER
                   ===================================================== */}

                <footer className="oppies-dashboard-footer">

                    <div>

                        © {new Date().getFullYear()} OPPIES — an{' '}

                        <a
                            href="https://www.ecliptek.co.nz"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            ECLIPTEK LTD
                        </a>{' '}

                        product.

                    </div>


                    <div>
                        Built for the Op Shop community.
                    </div>

                </footer>

            </div>

        </>
    );
}


export default Dashboard;